import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";

interface Connector {
  id: number;
  connectorId: number;
  status: string;
  errorCode: string | null;
  info: string | null;
  updatedAt: string;
}

interface Charger {
  id: number;
  cpId: string;
  vendor: string | null;
  model: string | null;
  serial: string | null;
  firmware: string | null;
  nominalPowerKw: number | null;
  connected: boolean;
  lastBootAt: string | null;
  lastHeartbeatAt: string | null;
  connectors: Connector[];
}

interface Tx {
  id: number;
  ocppTransactionId: number;
  cpId: string;
  connectorId: number;
  idTag: string;
  startTime: string;
  stopTime: string | null;
  energyWh: number | null;
  stopReason: string | null;
}

interface MeterValue {
  id: number;
  timestamp: string;
  valueWh: number;
  powerW: number | null;
  soc: number | null;
}

function fmt(iso: string | null) {
  return iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

function statusBadge(status: string) {
  const cls = ["nx-badge"];
  if (["Charging", "Preparing", "Finishing"].includes(status))
    cls.push("nx-badge--charging");
  else if (status === "Available") cls.push("nx-badge--available");
  else if (status === "Faulted" || status === "Unavailable")
    cls.push("nx-badge--fault");
  else cls.push("nx-badge--neutral");
  return <span className={cls.join(" ")}>{status}</span>;
}

/** SVG chart de linha simples (sem lib externa). */
function LineChart({ mvs }: { mvs: MeterValue[] }) {
  if (mvs.length < 2)
    return (
      <p style={{ color: "var(--nx-mute)" }}>Poucas medições para plotar.</p>
    );
  const width = 640;
  const height = 200;
  const pad = { l: 48, r: 12, t: 12, b: 28 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;

  const t0 = new Date(mvs[0]!.timestamp).getTime();
  const t1 = new Date(mvs[mvs.length - 1]!.timestamp).getTime();
  const y0 = Math.min(...mvs.map((m) => m.valueWh));
  const y1 = Math.max(...mvs.map((m) => m.valueWh));
  const yRange = Math.max(y1 - y0, 1);
  const tRange = Math.max(t1 - t0, 1);

  const points = mvs.map((m) => {
    const x = pad.l + ((new Date(m.timestamp).getTime() - t0) / tRange) * w;
    const y = pad.t + h - ((m.valueWh - y0) / yRange) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="220"
      role="img"
      aria-label="Energia acumulada"
    >
      <line
        x1={pad.l}
        y1={pad.t + h}
        x2={pad.l + w}
        y2={pad.t + h}
        stroke="var(--nx-line)"
      />
      <line
        x1={pad.l}
        y1={pad.t}
        x2={pad.l}
        y2={pad.t + h}
        stroke="var(--nx-line)"
      />
      <text
        x={pad.l - 6}
        y={pad.t + 10}
        textAnchor="end"
        fontSize="10"
        fill="var(--nx-mute)"
      >
        {(y1 / 1000).toFixed(1)} kWh
      </text>
      <text
        x={pad.l - 6}
        y={pad.t + h}
        textAnchor="end"
        fontSize="10"
        fill="var(--nx-mute)"
      >
        {(y0 / 1000).toFixed(1)} kWh
      </text>
      <polyline
        fill="none"
        stroke="var(--nx-orange)"
        strokeWidth="2"
        points={points.join(" ")}
      />
      {mvs.map((m, i) => {
        const x = pad.l + ((new Date(m.timestamp).getTime() - t0) / tRange) * w;
        const y = pad.t + h - ((m.valueWh - y0) / yRange) * h;
        return (
          <circle
            key={m.id}
            cx={x}
            cy={y}
            r={i === mvs.length - 1 ? 4 : 3}
            fill="var(--nx-orange)"
          />
        );
      })}
    </svg>
  );
}

export function ChargerDetailPage({ cpId }: { cpId: string }) {
  const { user } = useAuth();
  const canOperate = user?.role === "admin" || user?.role === "operator";
  const [charger, setCharger] = useState<Charger | null>(null);
  const [txs, setTxs] = useState<Tx[]>([]);
  const [selectedTx, setSelectedTx] = useState<number | null>(null);
  const [mvs, setMvs] = useState<MeterValue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [savingConfiguration, setSavingConfiguration] = useState(false);

  const load = useCallback(() => {
    fetch(`/api/chargers/${encodeURIComponent(cpId)}`, {
      credentials: "include",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { charger: Charger }) => setCharger(d.charger))
      .catch((e: Error) => setError(e.message));

    fetch(`/api/transactions?cpId=${encodeURIComponent(cpId)}&limit=20`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : { transactions: [] }))
      .then((d: { transactions: Tx[] }) => {
        setTxs(d.transactions);
        if (d.transactions[0] && selectedTx == null)
          setSelectedTx(d.transactions[0].ocppTransactionId);
      });
  }, [cpId, selectedTx]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (selectedTx == null) return;
    fetch(`/api/transactions/${selectedTx}/meter-values`, {
      credentials: "include",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { meterValues: MeterValue[] }) => setMvs(d.meterValues))
      .catch(() => setMvs([]));
  }, [selectedTx]);

  async function remoteStop(txId: number) {
    if (!confirm(`Parar transação ${txId}?`)) return;
    await fetch(`/api/chargers/${encodeURIComponent(cpId)}/remote-stop`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transactionId: txId }),
    });
    load();
  }

  async function command(path: "remote-start" | "reset" | "unlock", body: Record<string, unknown>, label: string) {
    if (!confirm(`${label} em ${cpId}?`)) return;
    setBusy(path);
    try {
      const response = await fetch(`/api/chargers/${encodeURIComponent(cpId)}/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null) as { error?: string } | null;
        alert(data?.error ?? `Erro ${response.status}`);
        return;
      }
      load();
    } finally {
      setBusy(null);
    }
  }

  async function saveConfiguration(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = String(form.get("nominalPowerKw") ?? "").trim();
    const nominalPowerKw = value === "" ? null : Number(value);
    if (nominalPowerKw !== null && (!Number.isInteger(nominalPowerKw) || nominalPowerKw < 1 || nominalPowerKw > 2000)) {
      alert("Informe uma potência nominal inteira entre 1 e 2000 kW.");
      return;
    }

    setSavingConfiguration(true);
    try {
      const response = await fetch(`/api/chargers/${encodeURIComponent(cpId)}/configuration`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nominalPowerKw }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null) as { error?: string } | null;
        alert(data?.error ?? `Erro ${response.status}`);
        return;
      }
      const data = await response.json() as { charger: Charger };
      setCharger(data.charger);
    } finally {
      setSavingConfiguration(false);
    }
  }

  if (error && !charger) return <div className="nx-error">Erro: {error}</div>;
  if (!charger) return <div className="nx-loader">Carregando…</div>;

  // "Última comunicação" no subtítulo: o heartbeat OCPP quando presente,
  // senão o boot. Traço se nunca comunicou — informar 0:00 seria falso.
  const lastComm = charger.lastHeartbeatAt ?? charger.lastBootAt;
  const lastCommTxt = lastComm
    ? new Date(lastComm).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <div>
      <p style={{ color: "var(--nx-mute)", fontSize: ".875rem" }}>
        <Link to="/app/chargers">← Carregadores</Link>
      </p>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Detalhe do carregador</span>
          <h1>
            <span className="mono">{charger.cpId}</span>
            <span
              className={
                charger.connected
                  ? "nx-badge nx-badge--available"
                  : "nx-badge nx-badge--fault"
              }
            >
              {charger.connected ? "online" : "offline"}
            </span>
          </h1>
          <div className="nx-page-head__sub">
            Última comunicação: {lastCommTxt}
          </div>
        </div>
      </div>

      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Vendor / Modelo</div>
          <div className="nx-kpi__value" style={{ fontSize: "1.2rem" }}>
            {charger.vendor ?? "—"} <br />
            {charger.model ?? "—"}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--cyan">
          <div className="nx-kpi__label">Firmware</div>
          <div className="nx-kpi__value mono" style={{ fontSize: "1.2rem" }}>
            {charger.firmware ?? "—"}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--orange">
          <div className="nx-kpi__label">Último heartbeat</div>
          <div className="nx-kpi__value" style={{ fontSize: "1.2rem" }}>
            {fmt(charger.lastHeartbeatAt)}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Potência nominal</div>
          <div className="nx-kpi__value" style={{ fontSize: "1.2rem" }}>
            {charger.nominalPowerKw == null ? "—" : `${charger.nominalPowerKw} kW`}
          </div>
        </div>
      </div>

      {canOperate && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2 style={{ fontSize: "1rem" }}>Configuração operacional</h2>
          <form onSubmit={(event) => void saveConfiguration(event)} className="nx-filters" style={{ alignItems: "end" }}>
            <label className="nx-field">
              <span>Potência nominal (kW)</span>
              <input name="nominalPowerKw" type="number" min="1" max="2000" step="1" defaultValue={charger.nominalPowerKw ?? ""} placeholder="Ex.: 150" />
            </label>
            <button className="nx-btn" type="submit" disabled={savingConfiguration}>
              {savingConfiguration ? "Salvando…" : "Salvar potência"}
            </button>
          </form>
          <p style={{ color: "var(--nx-mute)", fontSize: ".8rem", marginBottom: 0 }}>
            Informe a potência de placa do carregador. Este valor é usado nos indicadores de utilização e eficiência.
          </p>
        </div>
      )}

      <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
        <h2 style={{ fontSize: "1rem" }}>Conectores</h2>
        {charger.connectors.length === 0 ? (
          <p style={{ color: "var(--nx-mute)" }}>
            Nenhum conector reportado ainda.
          </p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>ErrorCode</th>
                  <th>Info</th>
                  <th>Atualizado em</th>
                </tr>
              </thead>
              <tbody>
                {charger.connectors.map((c) => (
                  <tr key={c.id}>
                    <td>#{c.connectorId}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td>{c.errorCode ?? "—"}</td>
                    <td>{c.info ?? "—"}</td>
                    <td>{fmt(c.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canOperate && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2 style={{ fontSize: "1rem" }}>Comandos remotos</h2>
          <div style={{ display: "flex", gap: "var(--nx-space-2)", flexWrap: "wrap" }}>
            <button className="nx-btn nx-btn--ghost-dark" type="button" disabled={busy !== null}
              onClick={() => void command("reset", { type: "Soft" }, "Reiniciar equipamento")}>
              {busy === "reset" ? "Enviando…" : "Reiniciar"}
            </button>
            {charger.connectors.filter((c) => c.connectorId > 0).map((c) => (
              <button key={c.id} className="nx-btn nx-btn--ghost-dark" type="button" disabled={busy !== null}
                onClick={() => void command("unlock", { connectorId: c.connectorId }, `Destravar conector #${c.connectorId}`)}>
                {busy === "unlock" ? "Enviando…" : `Destravar #${c.connectorId}`}
              </button>
            ))}
            {charger.connectors.filter((c) => c.connectorId > 0 && c.status === "Available").map((c) => (
              <button key={`start-${c.id}`} className="nx-btn" type="button" disabled
                title="Start remoto bloqueado. A recarga inicia no próprio carregador."
                onClick={() => void command("remote-start", { connectorId: c.connectorId, idTag: "CARD-DEMO-001" }, `Iniciar recarga no conector #${c.connectorId}`)}>
                {busy === "remote-start" ? "Enviando…" : `Iniciar #${c.connectorId}`}
              </button>
            ))}
          </div>
          <p style={{ color: "var(--nx-mute)", fontSize: ".8rem", marginBottom: 0 }}>
            Cada solicitação e sua resposta são registradas na auditoria. O equipamento pode recusar um comando por segurança ou capacidade OCPP.
          </p>
        </div>
      )}

      <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
        <h2 style={{ fontSize: "1rem" }}>Transações recentes</h2>
        {txs.length === 0 ? (
          <p style={{ color: "var(--nx-mute)" }}>Sem transações.</p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th></th>
                  <th>#</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Energia</th>
                  <th>Motivo</th>
                  {canOperate && <th></th>}
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setSelectedTx(t.ocppTransactionId)}
                    style={{
                      cursor: "pointer",
                      background:
                        selectedTx === t.ocppTransactionId
                          ? "var(--nx-bg-alt)"
                          : undefined,
                    }}
                  >
                    <td>
                      <input
                        type="radio"
                        name="tx"
                        checked={selectedTx === t.ocppTransactionId}
                        onChange={() => setSelectedTx(t.ocppTransactionId)}
                      />
                    </td>
                    <td className="mono">#{t.ocppTransactionId}</td>
                    <td>{fmt(t.startTime)}</td>
                    <td>{t.stopTime ? fmt(t.stopTime) : <em>em curso</em>}</td>
                    <td className="mono">
                      {t.energyWh == null
                        ? "—"
                        : (t.energyWh / 1000).toFixed(3) + " kWh"}
                    </td>
                    <td>{t.stopReason ?? "—"}</td>
                    {canOperate && (
                      <td>
                        {!t.stopTime && (
                          <button
                            className="nx-btn nx-btn--sm"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void remoteStop(t.ocppTransactionId);
                            }}
                          >
                            Stop
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedTx != null && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2 style={{ fontSize: "1rem" }}>
            Energia acumulada — transação #{selectedTx}{" "}
            <small style={{ color: "var(--nx-mute)", fontWeight: 400 }}>
              ({mvs.length} medições)
            </small>
          </h2>
          <LineChart mvs={mvs} />
        </div>
      )}
    </div>
  );
}
